import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { AppState } from '@store/store';
import { generateUniqueId } from '@utils/utilities';
import { Canceler } from 'axios';

export enum UPLOAD_STATE {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  INITIATED = 'INITIATED',
  NONE = 'NONE',
}

export type UploadState = {
  uploadResponse: null | Record<string, any>;
  upload_id: string;
  status: UPLOAD_STATE;
  cancelToken: Canceler | null;
  meta: {
    progress: number;
  };
};

export type ExportState = {
  response: {
    session_id: string;
    status: string;
    urls: string;
    video_size: string;
  };
};

export type UploadPayload = {
  uploadResponse?: null | Record<string, any>;
  cancelToken?: Canceler;
  status: UPLOAD_STATE;
};

export type UploadStateType = {
  uploadPayload: {
    file: File | null;
    payload_id: string;
  };
  uploadState: UploadState;
  showPreview: boolean;
  exportState: ExportState;
};

// INITIAL STATE
const initialUploadState: UploadStateType = {
  uploadPayload: {
    file: null,
    payload_id: '',
  },
  uploadState: {
    uploadResponse: null,
    upload_id: '',
    status: UPLOAD_STATE.NONE,
    cancelToken: null,
    meta: {
      progress: 0,
    },
  },
  exportState: {
    response: {
      session_id: '',
      status: '',
      urls: '',
      video_size: '',
    },
  },
  showPreview: false,
};

// REDUCER
const uploadSlice = createSlice({
  name: 'upload',
  initialState: initialUploadState,
  reducers: {
    START_VIDEO_UPLOAD: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        uploadState: {
          ...state.uploadState,
          upload_id: generateUniqueId(),
          formData: action.payload,
          status: UPLOAD_STATE.INITIATED,
        },
      };
    },
    UPDATE_UPLOAD_PAYLOAD: (
      state,
      action: PayloadAction<{ file: File; payload_id: string }>,
    ) => {
      return {
        ...state,
        uploadPayload: {
          ...action.payload,
        },
      };
    },
    UPDATE_VIDEO_UPLOAD: (state, action: PayloadAction<UploadPayload>) => {
      return {
        ...state,
        uploadState: {
          ...state.uploadState,
          ...action.payload,
        },
      };
    },
    UPDATE_VIDEO_UPLOAD_PROGRESS: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        uploadState: {
          ...state.uploadState,
          meta: {
            progress: action.payload,
          },
        },
      };
    },
    UPDATE_EXPORT_RESPONSE: (state, action) => {
      return {
        ...state,
        exportState: {
          response: action.payload,
        },
      };
    },
    TOGGLE_SHOW_PREVIEW: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showPreview: action.payload,
      };
    },
    RESET_EXPORT_STATE: state => {
      return {
        ...state,
        exportState: {
          response: {
            ...initialUploadState.exportState.response,
          },
        },
      };
    },
    RESET_UPLOAD_STATE: () => {
      return { ...initialUploadState };
    },
  },
});

export const getUploadFileDetails = createSelector(
  (state: AppState) => state.upload,
  state => state.uploadState,
);

export const getExportResponse = createSelector(
  (state: AppState) => state.upload,
  state => state.exportState.response,
);

export const getUploadPayload = createSelector(
  (state: AppState) => state.upload,
  state => state.uploadPayload,
);

export const showPreviewSelector = createSelector(
  (state: AppState) => state.upload,
  state => state.showPreview,
);

export const {
  START_VIDEO_UPLOAD,
  UPDATE_VIDEO_UPLOAD,
  UPDATE_VIDEO_UPLOAD_PROGRESS,
  TOGGLE_SHOW_PREVIEW,
  UPDATE_EXPORT_RESPONSE,
  RESET_UPLOAD_STATE,
  UPDATE_UPLOAD_PAYLOAD,
  RESET_EXPORT_STATE,
} = uploadSlice.actions;

export default uploadSlice.reducer;
